import { useState, useEffect } from 'react';
import { calculationAPI, leadCalculationAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

export const useCalculations = () => {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referenceData, setReferenceData] = useState(null);

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const data = await calculationAPI.getReferenceData();
      setReferenceData(data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
      toast({
        title: "Error",
        description: "Failed to load form data. Using defaults.",
        variant: "destructive",
      });
    }
  };

  const createCalculation = async (calculationData) => {
    setLoading(true);
    try {
      const result = await calculationAPI.calculate(calculationData);
      
      // Add to local state
      setCalculations(prev => [result, ...prev]);
      
      toast({
        title: "Success",
        description: "Calculation completed successfully!",
      });
      
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to calculate attrition cost';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const submitLeadAndCalculation = async (leadData, calculationData) => {
    setLoading(true);
    try {
      const result = await leadCalculationAPI.submitLeadAndCalculation(leadData, calculationData);
      
      // Add calculation to local state
      setCalculations(prev => [result.calculation, ...prev]);
      
      toast({
        title: "Success",
        description: "Thank you! Your calculation results are ready.",
      });
     
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to process your request';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadCalculationHistory = async () => {
    setLoading(true);
    try {
      const history = await calculationAPI.getHistory(20);
      setCalculations(history);
    } catch (error) {
      console.error('Failed to load calculation history:', error);
      toast({
        title: "Error",
        description: "Failed to load calculation history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCalculation = async (calculationId) => {
    try {
      await calculationAPI.deleteCalculation(calculationId);
      
      // Remove from local state
      setCalculations(prev => prev.filter(calc => calc.id !== calculationId));
      
      toast({
        title: "Success",
        description: "Calculation deleted successfully",
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete calculation';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return {
    calculations,
    loading,
    referenceData,
    createCalculation,
    submitLeadAndCalculation,  // New method for lead magnet
    loadCalculationHistory,
    deleteCalculation,
    loadReferenceData,
  };
};